import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Avatar, Input, notification as antNotification } from "antd";
import {
  ArrowLeftOutlined,
  CustomerServiceOutlined,
  LogoutOutlined,
  MenuOutlined,
  QuestionCircleOutlined,
  SearchOutlined,
  SettingOutlined,
  UserOutlined,
} from "@ant-design/icons";
import OlitaskHeaderIcon from "components/olitask/OlitaskHeaderIcon";
import { useAuth } from "contexts/AuthContext";
import { useWorkspace } from "contexts/WorkspaceContext";
import paths from "routes/paths";
import {
  fetchAllNotifications,
  fetchUnreadNotificationCount,
} from "services/olitaskNotificationService";
import { getUserDisplayName } from "utils/taskAssignee";
import WorkspacePicker from "workspaces/WorkspacePicker";
import { OLIVEEB_COLORS } from "workspaces/colors";

const NOTIFICATION_POLL_INTERVAL_MS = 5000;
const SHOWN_NOTIFICATION_IDS_KEY = "shown_notification_ids_v1";

const notificationIsCreated = (notification) =>
  String(notification?.status || "").toUpperCase() === "CREATED";

const parseNotificationData = (value) => {
  if (!value) return {};
  if (typeof value === "object") return value;
  try {
    return JSON.parse(value);
  } catch {
    return {};
  }
};

const rememberFreshNotifications = (items) => {
  if (typeof window === "undefined" || !items.length) return [];

  try {
    const raw = window.localStorage.getItem(SHOWN_NOTIFICATION_IDS_KEY);
    const seen = new Set(raw ? JSON.parse(raw) : []);
    const fresh = items.filter((item) => item?.id && !seen.has(String(item.id)));
    if (fresh.length) {
      fresh.forEach((item) => seen.add(String(item.id)));
      window.localStorage.setItem(
        SHOWN_NOTIFICATION_IDS_KEY,
        JSON.stringify(Array.from(seen).slice(-2000)),
      );
    }
    return fresh;
  } catch {
    return items;
  }
};

export default function Topbar({ onToggleMobile }) {
  const { logout, user } = useAuth();
  const { selectedWorkspaceId, selectedWorkspaceUse } = useWorkspace();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState("");
  const [ddOpen, setDdOpen] = useState(false);
  const [notificationCounts, setNotificationCounts] = useState({
    ACTIVITY: 0,
    CHAT: 0,
    EMAIL: 0,
  });
  const menuRef = useRef(null);
  const isPollingRef = useRef(false);
  const countSeqRef = useRef(0);
  const actorId = user?.id || user?.userId || "";

  const displayName =
    getUserDisplayName(user) || user?.fullName || user?.username || "Account";
  const avatarSrc =
    typeof user?.profilePhoto === "string" &&
    /^https?:\/\//i.test(user.profilePhoto)
      ? user.profilePhoto
      : typeof user?.imageUrl === "string" &&
          /^https?:\/\//i.test(user.imageUrl)
        ? user.imageUrl
        : null;
  const avatarInitials = useMemo(() => {
    const first = String(user?.firstName || "").trim();
    const last = String(user?.lastName || "").trim();
    if (first || last) return `${first[0] || ""}${last[0] || ""}`.toUpperCase();
    const parts = String(displayName || "")
      .trim()
      .split(/\s+/)
      .filter(Boolean);
    return `${parts[0]?.[0] || ""}${parts[1]?.[0] || ""}`.toUpperCase() || "U";
  }, [displayName, user?.firstName, user?.lastName]);

  const refreshNotificationCounts = useCallback(async () => {
    const userStatus = String(user?.status || "").toUpperCase();
    if (!actorId || !selectedWorkspaceId || (userStatus && userStatus !== "ACTIVE")) {
      setNotificationCounts({ ACTIVITY: 0, CHAT: 0, EMAIL: 0 });
      return;
    }

    const requestSeq = countSeqRef.current + 1;
    countSeqRef.current = requestSeq;

    try {
      const [activity, chat, email] = await Promise.all([
        fetchUnreadNotificationCount({
          userId: actorId,
          category: "ACTIVITY",
          status: "CREATED",
          targetRole: selectedWorkspaceUse,
          workspaceId: selectedWorkspaceId,
        }),
        fetchUnreadNotificationCount({
          userId: actorId,
          category: "CHAT",
          status: "CREATED",
          targetRole: selectedWorkspaceUse,
          workspaceId: selectedWorkspaceId,
        }),
        fetchUnreadNotificationCount({
          userId: actorId,
          category: "EMAIL",
          status: "CREATED",
          targetRole: selectedWorkspaceUse,
          workspaceId: selectedWorkspaceId,
        }),
      ]);
      if (requestSeq !== countSeqRef.current) return;
      setNotificationCounts({
        ACTIVITY: activity,
        CHAT: chat,
        EMAIL: email,
      });
    } catch {
      // Keep the current badge state on intermittent poll errors, matching the mobile poller.
    }
  }, [actorId, selectedWorkspaceId, selectedWorkspaceUse, user?.status]);

  const showFreshNotifications = useCallback((items) => {
    const freshItems = rememberFreshNotifications(items.filter(notificationIsCreated));
    freshItems.forEach((item) => {
      const data = parseNotificationData(item.data);
      const category = String(item.category || data.category || "ACTIVITY").toUpperCase();
      const title =
        data.title ||
        item.title ||
        (category === "CHAT" ? "New chat message" : "Olitask update");
      const description =
        data.message ||
        item.message ||
        data.body ||
        data.summary ||
        "You have a new notification";

      antNotification.open({
        key: item.id,
        message: title,
        description,
        duration: 6,
        placement: "topRight",
        onClick: () => {
          const notificationPathCategory = ["ACTIVITY", "CHAT", "EMAIL"].includes(category)
            ? category
            : "ACTIVITY";
          navigate(paths.notifications(notificationPathCategory));
        },
      });

      if (window.Notification?.permission === "granted") {
        try {
          const browserNotification = new window.Notification(title, {
            body: description,
            tag: String(item.id),
          });
          browserNotification.onclick = () => {
            window.focus();
            const notificationPathCategory = ["ACTIVITY", "CHAT", "EMAIL"].includes(category)
              ? category
              : "ACTIVITY";
            navigate(paths.notifications(notificationPathCategory));
            browserNotification.close();
          };
        } catch {
          // Browser notifications are opportunistic; the in-app notification above is the fallback.
        }
      }
    });
  }, [navigate]);

  const refreshAllNotifications = useCallback(async () => {
    const userStatus = String(user?.status || "").toUpperCase();
    if (!actorId || !selectedWorkspaceId || (userStatus && userStatus !== "ACTIVE")) {
      return;
    }

    try {
      const data = await fetchAllNotifications({
        userId: actorId,
        targetRole: selectedWorkspaceUse,
        page: 0,
        size: 20,
        workspaceId: selectedWorkspaceId,
      });
      const nextNotifications = Array.isArray(data) ? data : [];
      showFreshNotifications(nextNotifications);
    } catch {
      // Keep polling; the next tick may succeed.
    }
  }, [actorId, selectedWorkspaceId, selectedWorkspaceUse, showFreshNotifications, user?.status]);

  const pollNotifications = useCallback(async () => {
    if (isPollingRef.current) return;
    isPollingRef.current = true;
    try {
      await Promise.all([refreshNotificationCounts(), refreshAllNotifications()]);
    } finally {
      isPollingRef.current = false;
    }
  }, [refreshAllNotifications, refreshNotificationCounts]);

  useEffect(() => {
    pollNotifications();
    const intervalId = window.setInterval(pollNotifications, NOTIFICATION_POLL_INTERVAL_MS);
    return () => {
      window.clearInterval(intervalId);
    };
  }, [pollNotifications]);

  useEffect(() => {
    const refreshWhenActive = () => {
      if (document.visibilityState === "visible") {
        pollNotifications();
      }
    };

    document.addEventListener("visibilitychange", refreshWhenActive);
    window.addEventListener("focus", pollNotifications);
    return () => {
      document.removeEventListener("visibilitychange", refreshWhenActive);
      window.removeEventListener("focus", pollNotifications);
    };
  }, [pollNotifications]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(event.target)) {
        setDdOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const profileMenuItems = [
    {
      label: "Profile",
      description: "View your Oliveeb identity",
      icon: <UserOutlined />,
      tone: "blue",
      onClick: () => {
        setDdOpen(false);
        navigate(paths.profile);
      },
    },
    {
      label: "Settings",
      description: "Tune your workspace experience",
      icon: <SettingOutlined />,
      tone: "yellow",
      onClick: () => {
        setDdOpen(false);
        navigate(paths.settings);
      },
    },
    {
      label: "Help",
      description: "Guides, answers, and tips",
      icon: <QuestionCircleOutlined />,
      tone: "blue",
      onClick: () => {
        setDdOpen(false);
        navigate(paths.help);
      },
    },
    {
      label: "Support",
      description: "Get help from Oliveeb",
      icon: <CustomerServiceOutlined />,
      tone: "orange",
      onClick: () => {
        setDdOpen(false);
        navigate(paths.support);
      },
    },
  ];

  const handleBack = (event) => {
    event.stopPropagation();
    if (window.history.length > 1 && location.key !== "default") {
      navigate(-1);
      return;
    }
    navigate(paths.home);
  };

  const shareOlitask = async () => {
    const shareUrl = `${window.location.origin}/download`;
    const shareText = "Join me on Olitask - get help, help others, or donate support in your community.";
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Share Olitask",
          text: shareText,
          url: shareUrl,
        });
        return;
      } catch {
        return;
      }
    }
    try {
      await navigator.clipboard?.writeText(shareUrl);
    } catch {
      window.location.href = shareUrl;
    }
  };

  const badgeText = (value) => {
    const count = Number(value || 0);
    if (count > 99) return "99+";
    return String(count);
  };

  return (
    <header className="pm-t" onClick={() => setDdOpen(false)}>
      <div className="pm-topbar-row d-flex justify-content-between align-items-center w-100">
        <div className="pm-topbar-left d-flex align-items-center flex-grow-1 min-w-0">
          <button
            type="button"
            className="pm-mobile-toggle"
            onClick={onToggleMobile}
            aria-label="Open menu"
          >
            <MenuOutlined />
          </button>
          <button
            type="button"
            className="pm-mobile-brand"
            onClick={() => navigate(paths.home)}
            aria-label="Olitask home"
            title="Olitask home"
          >
            <img src="/logo.png" alt="" />
          </button>
          <button
            type="button"
            className="pm-back-btn"
            onClick={handleBack}
            aria-label="Go back"
            title="Back"
          >
            <ArrowLeftOutlined />
            <span>Back</span>
          </button>
          <Input
            prefix={<SearchOutlined style={{ color: "#cbd5e1", fontSize: 18 }} />}
            placeholder="Search..."
            className="pm-topsearch"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            allowClear
          />
        </div>
        <div className="pm-topbar-right ms-auto d-flex align-items-center">
          <div className="pm-acts">
            <WorkspacePicker />

            <div className="pm-dv pm-dv-mobile-break" />

            <div className="pm-secondary-actions">
              <button
                type="button"
                className="pm-mobile-action pm-mobile-action-share"
                title="Share Olitask"
                aria-label="Share Olitask"
                onClick={shareOlitask}
              >
                <OlitaskHeaderIcon name="share" size={25} />
              </button>
              <button
                type="button"
                className="pm-mobile-action"
                title="Actionables"
                aria-label="Actionables"
                onClick={() => navigate(paths.actionables)}
              >
                <OlitaskHeaderIcon name="flash" size={27} />
                <span className="pm-action-badge">0</span>
              </button>
              <button
                type="button"
                className="pm-mobile-action"
                title="Chats"
                aria-label="Chats"
                onClick={() => navigate(paths.notifications("CHAT"))}
              >
                <OlitaskHeaderIcon name="chat" size={27} />
                <span className="pm-action-badge">{badgeText(notificationCounts.CHAT)}</span>
              </button>
              <button
                type="button"
                className="pm-mobile-action"
                title="Messages"
                aria-label="Messages"
                onClick={() => navigate(paths.notifications("EMAIL"))}
              >
                <OlitaskHeaderIcon name="mail" size={27} />
                <span className="pm-action-badge">{badgeText(notificationCounts.EMAIL)}</span>
              </button>
              <button
                type="button"
                className="pm-mobile-action"
                title="Notifications"
                aria-label="Notifications"
                onClick={() => navigate(paths.notifications("ACTIVITY"))}
              >
                <OlitaskHeaderIcon name="notification" size={27} />
                <span className="pm-action-badge">{badgeText(notificationCounts.ACTIVITY)}</span>
              </button>

              <div className="pm-dv" />

              <div
                className="pm-avw"
                ref={menuRef}
                onClick={(e) => {
                  e.stopPropagation();
                  setDdOpen((d) => !d);
                }}
              >
                <div className="pm-av">
                  <Avatar
                    src={avatarSrc}
                    style={{
                      width: "100%",
                      height: "100%",
                      borderRadius: "999px",
                      background: avatarSrc
                        ? undefined
                        : `linear-gradient(135deg, ${OLIVEEB_COLORS.oliveebDarkBlue}, ${OLIVEEB_COLORS.oliveebBlue})`,
                      color: OLIVEEB_COLORS.white,
                      fontSize: 12,
                      fontWeight: 700,
                    }}
                  >
                    {!avatarSrc ? avatarInitials : null}
                  </Avatar>
                </div>

                {ddOpen && (
                  <div className="pm-dd" onClick={(e) => e.stopPropagation()}>
                  <div className="pm-ddh">
                    <div className="pm-dd-avatar">
                      <Avatar
                        src={avatarSrc}
                        style={{
                          width: "100%",
                          height: "100%",
                          borderRadius: "999px",
                          background: avatarSrc
                            ? undefined
                            : `linear-gradient(135deg, ${OLIVEEB_COLORS.oliveebGreen}, ${OLIVEEB_COLORS.oliveebBlue})`,
                          color: OLIVEEB_COLORS.white,
                          fontSize: 16,
                          fontWeight: 900,
                        }}
                      >
                        {!avatarSrc ? avatarInitials : null}
                      </Avatar>
                    </div>
                    <div className="pm-dd-user">
                      <div className="pm-dd-kicker">Signed in as</div>
                      <div className="pm-ddn">{displayName}</div>
                      <div className="pm-dde">{user?.email || "Oliveeb account"}</div>
                    </div>
                  </div>
                  <div className="pm-dd-list">
                    {profileMenuItems.map((item) => (
                      <div
                        key={item.label}
                        className="pm-ddi pm-dd-action"
                        onClick={() => item.onClick?.()}
                        data-tone={item.tone}
                        style={{ cursor: item.onClick ? "pointer" : "default" }}
                      >
                        <span className="pm-dd-ico">{item.icon}</span>
                        <span className="pm-dd-copy">
                          <span className="pm-dd-label">{item.label}</span>
                          <span className="pm-dd-desc">{item.description}</span>
                        </span>
                      </div>
                    ))}
                  </div>
                  <div
                    className="pm-ddi pm-dd-action dng mt-2"
                    onClick={() => logout(false, paths.landing)}
                    style={{ cursor: "pointer" }}
                  >
                    <span className="pm-dd-ico"><LogoutOutlined /></span>
                    <span className="pm-dd-copy">
                      <span className="pm-dd-label">Sign out</span>
                      <span className="pm-dd-desc">End this browser session</span>
                    </span>
                  </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
